import { Platform } from "react-native";

export type ShopifyMedia = {
  alt: string;
  mediaContentType: "IMAGE";
  originalSource: string;
};

export type ShopifyProductOption = {
  name: string;
  values: {
    name: string;
  };
};

export type ShopifyProductPayload = {
  title: string;
  price: number;
  tags: string;
  media?: ShopifyMedia;
  productOptions: ShopifyProductOption[];
};

export type ShopifyCreateRequest = {
  products: ShopifyProductPayload[];
};

export type ShopifyProductVariant = {
  id: string;
  title?: string;
  price?: string;
};

export type ShopifyProduct = {
  id?: string;
  title?: string;
  variants: ShopifyProductVariant[];
};

export type ShopifyCreateProductResponse = {
  product: ShopifyProduct[];
  error_code?: number;
  error_message?: string;
};

export type ShopifyCartProperties = Record<string, string | number>;

export type ShopifyCartProduct = {
  id: string;
  quantity: number;
  properties?: ShopifyCartProperties;
};

export type ConfigContext = {
  config?: Record<string, any>;
  personalization?: Record<string, any>;
  displayConfiguration?: Array<{ option_type: string; option_name: string }>;
  slugDetail?: Record<string, any>;
  labelConfig?: Record<string, string>;
};

const formatValue = (val: any): string => {
  if (typeof val === "string") {
    return val
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.toLowerCase().slice(1))
      .join(" ");
  }
  return String(val);
};

export const buildShopifyCartProperties = (
  configContext: ConfigContext
): ShopifyCartProperties => {
  const {
    config,
    personalization,
    displayConfiguration = [],
    slugDetail,
    labelConfig = {},
  } = configContext;

  const properties: ShopifyCartProperties = {};
  const rawConfig =
    config && Object.keys(config).length > 0 ? config : slugDetail;

  displayConfiguration.forEach((option) => {
    const value = rawConfig?.[option.option_type];
    if (value !== undefined) {
      properties[option.option_name] = formatValue(value);
    }
  });

  if (rawConfig) {
    Object.keys(rawConfig).forEach((key) => {
      if (!displayConfiguration.find((option) => option.option_type === key)) {
        properties[key] = formatValue(rawConfig[key]);
      }
    });
  }

  if (personalization) {
    if (personalization.reference_po) {
      properties[labelConfig.reference_po || "Reference PO"] =
        personalization.reference_po;
    }
    if (personalization.engrave_font) {
      properties[labelConfig.engrave_font || "Engrave Font"] =
        personalization.engrave_font;
    }
    if (personalization.engrave_text) {
      properties[labelConfig.engrave_text || "Engrave Text"] =
        personalization.engrave_text;
    }
    if (personalization.special_instructions) {
      properties[labelConfig.special_instructions || "Special Instructions"] =
        personalization.special_instructions;
    }
  }

  return properties;
};

export const addToShopifyCart = (
  variantId: string,
  quantity: number,
  properties?: ShopifyCartProperties
): void => {
  if (Platform.OS === "web" && typeof window !== "undefined") {
    try {
      const product: ShopifyCartProduct = { id: variantId, quantity };
      if (properties && Object.keys(properties).length > 0) {
        product.properties = properties;
      }
      window.parent.postMessage({ type: "add-to-cart", product }, "*");
    } catch (error) {
      console.error("Failed to add to Shopify cart:", error);
    }
  }
};

export const buildShopifyCreatePayload = (
  itemDetail: any
): ShopifyCreateRequest => {
  const firstImage = itemDetail?.media?.find(
    (item: any) => item.type === "image"
  );

  const media: ShopifyMedia | undefined = firstImage
    ? {
        alt: "Image",
        mediaContentType: "IMAGE",
        originalSource: firstImage.link || firstImage.url,
      }
    : undefined;

  const product: ShopifyProductPayload = {
    title: itemDetail?.long_title || itemDetail?.title || "",
    price: itemDetail?.total_sales_price || itemDetail?.price || 0,
    tags: `twm_ifp, Stock Number - ${itemDetail?.stock_num || ""}, ID - ${
      itemDetail?.id || ""
    }`,
    productOptions: [
      {
        name: "Stock Number",
        values: {
          name: itemDetail?.stock_num || "",
        },
      },
    ],
  };

  if (media) {
    product.media = media;
  }

  return {
    products: [product],
  };
};

export const isIframeContext = (): boolean => {
  if (Platform.OS !== "web" || typeof window === "undefined") {
    return false;
  }

  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};
